import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { C, EASE_SPRING, glass, ink } from '../lib/theme';
import { IconAlert, IconCheck } from './icons';

type ToastKind = 'ok' | 'error' | 'info';
interface ToastItem { id: number; kind: ToastKind; text: string }

const ToastCtx = createContext<(text: string, kind?: ToastKind) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

const TONE: Record<ToastKind, { color: string; icon: ReactNode }> = {
  ok: { color: C.green, icon: <IconCheck size={14} /> },
  error: { color: C.churn, icon: <IconAlert size={14} /> },
  info: { color: C.accent, icon: 'i' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((text: string, kind: ToastKind = 'info') => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, kind, text }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 4600);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div style={{ position: 'fixed', bottom: 26, right: 26, zIndex: 300, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            style={{
              ...glass, borderRadius: 14, padding: '12px 16px', maxWidth: 380,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: `koSlideIn 0.24s ${EASE_SPRING}`, pointerEvents: 'auto',
            }}
          >
            <span style={{
              width: 18, height: 18, borderRadius: '50%', flex: 'none', marginTop: 1,
              background: `${TONE[t.kind].color}26`, color: TONE[t.kind].color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800,
            }}>{TONE[t.kind].icon}</span>
            <span style={{ fontSize: 13, color: ink(0.88), lineHeight: 1.45 }}>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
